import L from "leaflet";
import * as turf from "@turf/turf";
import { renderLinePlanningPanel } from "./ui.js";

export const linePlanningTool = {
  id: "line-planning",
  name: "Line Planning",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy(); this._rt = null; },
  renderPanel(app, containerEl) { if (!this._rt) this._rt = createRuntime(app); this._rt.mount(containerEl); },
  serialize() { return this._rt?.snapshot() ?? null; },
  restore(app, data) { if (!this._rt) this._rt = createRuntime(app); this._rt.restore(data); },
};

function createRuntime(app) {
  const map = app.map.instance;
  const mapEl = app.map.el;
  const draft = L.featureGroup().addTo(map);
  const plansRoot = L.featureGroup().addTo(map);
  const sketch = L.featureGroup().addTo(map);
  const plans = [];
  const selected = new Set();
  let activeId = null;
  let seq = 1;
  let aoi = null;
  let lines = [];
  let labels = [];
  let container = null;
  let autoTimer = null;
  let draftDA = null;
  let stage = "mode";
  let editingPlanId = null;
  let inGlobalSync = false;
  const el = {};

  // ── Statistical Estimator state ──────────────────────────────────────────
  const ETOPO_SAMPLES_URL = "https://gis.ngdc.noaa.gov/arcgis/rest/services/DEM_mosaics/DEM_global_mosaic/ImageServer/getSamples";
  const GEBCO_SAMPLES_URL = "https://tiles.arcgis.com/tiles/C8EMgrsFcRFL6LrL/arcgis/rest/services/GEBCO_elevation_wgs84/ImageServer/getSamples";
  const statAOIs = [];   // { id, name, layer, turfPoly, areaAcres, depthFtOverride, loading, error, result }
  let statSeq = 1;
  const statEl = {};     // cached DOM refs for stat section

  function $(id) { return container.querySelector(`#${id}`); }
  function setStatus(s) { if (el.status) el.status.textContent = s; }
  function setCount(n) { if (el.count) el.count.textContent = `${n} lines`; }
  function heading() { const v = Number(el.heading?.value || 0); return ((v % 360) + 360) % 360; }
  function spacing() { return Math.max(1, Number(el.spacing?.value || 50) || 50); }
  function setHeading(v) { const h = Math.max(0, Math.min(359, Math.round(v))); if (el.heading) el.heading.value = `${h}`; if (el.headingS) el.headingS.value = `${h}`; if (el.headingVal) el.headingVal.textContent = `${h}\u00b0`; }
  function setSpacing(v) { const s = Math.max(1, Math.round(v)); if (el.spacing) el.spacing.value = `${s}`; if (el.spacingS) el.spacingS.value = `${Math.max(1, Math.min(500, s))}`; if (el.spacingVal) el.spacingVal.textContent = `${s} m`; }
  function current() { if (selected.size > 1) return { mode: "multi", plans: plans.filter((p) => selected.has(p.id)) }; const p = plans.find((x) => x.id === activeId); return p ? { mode: "plan", plan: p } : { mode: "draft" }; }
  function clearLabels() { labels.forEach((m) => { try { draft.removeLayer(m); } catch {} }); labels = []; }
  function syncCursor(on) { if (mapEl) mapEl.style.cursor = on ? "crosshair" : ""; }
  function esc(s) { return String(s).replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch])); }
  function clearPlanLabels(plan) {
    if (!plan?._labelMarkers?.length) return;
    plan._labelMarkers.forEach((m) => { try { plan.group.removeLayer(m); } catch {} });
    plan._labelMarkers = [];
  }
  function buildPlanLabels(plan) {
    clearPlanLabels(plan);
    if (!plan || !plan._labelsEnabled) return;
    const next = [];
    (plan.features || []).forEach((f, i) => {
      const len = turf.length(f, { units: "kilometers" });
      if (!(len > 0)) return;
      const mid = turf.along(f, len / 2, { units: "kilometers" });
      const [lng, lat] = mid.geometry.coordinates || [];
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const txt = esc(f?.properties?.name || `${i + 1}`);
      const marker = L.marker([lat, lng], {
        interactive: false,
        keyboard: false,
        icon: L.divIcon({ className: "lp-line-label", html: `<span>${txt}</span>` }),
      }).addTo(plan.group);
      next.push(marker);
    });
    plan._labelMarkers = next;
  }

  function planVisible(plan) {
    return !!(plan?.group && map.hasLayer(plan.group));
  }

  function applyPlanOpacity(plan, opacity01) {
    if (!plan) return;
    const o = Math.max(0, Math.min(1, Number(opacity01) || 0));
    plan.opacity = o;
    const lineOpacity = Math.max(0, Math.min(1, o));
    const fillOpacity = Math.max(0, Math.min(1, 0.08 * o));
    plan.group.eachLayer((l) => {
      if (l?.setStyle) {
        try {
          if (l === plan.aoiLayer) l.setStyle({ opacity: Math.max(0, 0.9 * lineOpacity), fillOpacity });
          else l.setStyle({ opacity: lineOpacity });
        } catch {}
      } else if (l?.setOpacity) {
        try { l.setOpacity(lineOpacity); } catch {}
      }
    });
  }

  function syncGlobalPlan(plan) {
    if (!plan?.globalLayerId || inGlobalSync) return;
    app.services.layers.updateLayer(plan.globalLayerId, {
      name: plan.name,
      visible: planVisible(plan),
      opacity: plan.opacity ?? 1,
      meta: { featureCount: plan.features?.length || 0 },
    });
  }

  function removePlan(id, { fromGlobal = false } = {}) {
    const i = plans.findIndex((x) => x.id === id);
    if (i === -1) return;
    const p = plans[i];
    clearPlanLabels(p);
    try { map.removeLayer(p.group); } catch {}
    try { plansRoot.removeLayer(p.group); } catch {}
    plans.splice(i, 1);
    selected.delete(id);
    if (activeId === id) activeId = null;
    if (!fromGlobal && p?.globalLayerId) {
      inGlobalSync = true;
      try { app.services.layers.removeLayer(p.globalLayerId); } finally { inGlobalSync = false; }
    }
    renderPlans();
    updateExportSelectedEnabled();
  }

  function registerGlobalPlan(plan) {
    if (!plan || plan.globalLayerId) return;
    plan.opacity = Number.isFinite(Number(plan.opacity)) ? Number(plan.opacity) : 1;
    plan.globalLayerId = app.services.layers.addLayer("line-planning", {
      name: plan.name,
      kind: "overlay",
      group: "Line Planning",
      type: "managed",
      visible: planVisible(plan),
      opacity: plan.opacity,
      volatile: true,
      meta: { featureCount: plan.features?.length || 0 },
      managed: {
        setVisible(v) {
          if (v) {
            plansRoot.addLayer(plan.group);
            plan.group.addTo(map);
            if (plan._labelsEnabled) buildPlanLabels(plan);
          } else {
            map.removeLayer(plan.group);
          }
          if (!inGlobalSync) renderPlans();
        },
        setOpacity(o) {
          applyPlanOpacity(plan, o);
          if (!inGlobalSync) renderPlans();
        },
        getBounds() {
          const b = plan.group?.getBounds?.();
          return b?.isValid?.() ? b : null;
        },
        bringToFront() {
          try { plan.group?.bringToFront?.(); } catch {}
        },
        remove() {
          if (inGlobalSync) return;
          inGlobalSync = true;
          try { removePlan(plan.id, { fromGlobal: true }); } finally { inGlobalSync = false; }
        },
      },
    });
  }

  function mount(next) {
    if (container === next) return;
    container = next;
    renderLinePlanningPanel(container);
    el.draw = $("lp-draw"); el.gen = $("lp-generate"); el.save = $("lp-save"); el.clear = $("lp-clear");
    el.mode = $("lp-mode"); el.source = $("dap-source"); el.angle = $("dap-angle"); el.overlap = $("dap-overlap"); el.dap = $("dap-status");
    el.heading = $("lp-heading"); el.spacing = $("lp-spacing"); el.headingS = $("lp-heading-slider"); el.spacingS = $("lp-spacing-slider");
    el.headingVal = $("lp-heading-val"); el.spacingVal = $("lp-spacing-val"); el.extend = $("lp-extend"); el.prefix = $("lp-prefix"); el.start = $("lp-start");
    el.speed = $("lp-vessel-speed"); el.status = $("lp-status"); el.count = $("lp-count"); el.planList = $("lp-planlist");
    el.exportSel = $("lp-export-selected"); el.exportFmt = $("lp-export-format"); el.daRow = $("lp-depthaware-row");
    bind();
    setHeading(heading()); setSpacing(spacing()); applyMode();
    setStage("mode");
    renderPlans(); setStatus("ready"); setCount(lines.length);
    bindStatSection();
  }

  function bind() {
    el.draw?.addEventListener("click", drawAOI);
    el.gen?.addEventListener("click", () => (el.mode?.value === "depth" ? runDepthAware() : generate(false)));
    el.save?.addEventListener("click", saveDraft);
    el.clear?.addEventListener("click", clearAll);
    el.exportSel?.addEventListener("click", exportSelected);
    el.mode?.addEventListener("change", applyMode);
    el.heading?.addEventListener("input", () => { setHeading(heading()); scheduleAuto(); });
    el.headingS?.addEventListener("input", () => { setHeading(Number(el.headingS.value)); scheduleAuto(); });
    el.spacing?.addEventListener("input", () => { setSpacing(spacing()); scheduleAuto(); });
    el.spacingS?.addEventListener("input", () => { setSpacing(Number(el.spacingS.value)); scheduleAuto(); });
    el.extend?.addEventListener("input", scheduleAuto);
  }

  function applyMode() {
    const isDA = (el.mode?.value || "manual") === "depth";
    el.daRow?.classList.toggle("open", isDA);
    el.daRow?.setAttribute("aria-hidden", isDA ? "false" : "true");
    const canEdit = !isDA || stage === "drafted" || !!activeId;
    if (el.spacing) el.spacing.disabled = !canEdit;
    if (el.spacingS) el.spacingS.disabled = !canEdit;
    setStatus(isDA ? "Depth Aware: ready" : "Manual: ready");
  }

  function clearEmphasis() {
    [el.draw, el.gen, el.save, el.clear].forEach((b) => {
      if (!b) return;
      b.classList.remove("primary");
      b.classList.add("secondary");
    });
  }

  function emphasize(btn) {
    if (!btn) return;
    btn.classList.add("primary");
    btn.classList.remove("secondary");
  }

  function setStage(next) {
    stage = next || "mode";
    clearEmphasis();
    if (el.save) el.save.disabled = true;
    if (stage === "mode") emphasize(el.draw);
    if (stage === "aoi") emphasize(el.gen);
    if (stage === "drafted") {
      if (el.save) el.save.disabled = false;
      emphasize(el.save);
      emphasize(el.clear);
    }
    applyMode();
  }

  function clearDraft() {
    sketch.clearLayers(); draft.clearLayers(); aoi = null; lines = []; clearLabels();
    setCount(0); setStatus("Draft cleared");
    if (el.save) el.save.disabled = true;
  }
  function clearAll() { activeId = null; selected.clear(); clearDraft(); renderPlans(); syncCursor(false); setStage("mode"); }

  function drawAOI() {
    activeId = null; selected.clear(); clearDraft(); renderPlans();
    setStatus("Draw AOI: click to add vertices, double-click to finish");
    const was = map.doubleClickZoom?.enabled?.() ?? false; map.doubleClickZoom?.disable?.(); syncCursor(true);
    const pts = []; const temp = L.polygon([], { color: "#a855f7", weight: 2, opacity: 0.8, fillOpacity: 0.1, dashArray: "6 4" }).addTo(sketch);
    const onClick = (e) => { pts.push(e.latlng); temp.setLatLngs(pts); };
    const onDbl = (e) => {
      L.DomEvent.stop(e); map.off("click", onClick); map.off("dblclick", onDbl); sketch.removeLayer(temp); if (was) setTimeout(() => map.doubleClickZoom?.enable?.(), 0); syncCursor(false);
      if (pts.length < 3) { setStatus("AOI needs >= 3 vertices"); return; }
      aoi = L.polygon(pts, { color: "#a855f7", weight: 2, opacity: 0.9, fillOpacity: 0.1 }).addTo(draft);
      setStatus("AOI set");
      setStage("aoi");
    };
    map.on("click", onClick); map.on("dblclick", onDbl);
  }

  function polyFromAOI(layer) {
    const latlngs = layer.getLatLngs()[0] || layer.getLatLngs();
    const ring = latlngs.map((p) => [p.lng, p.lat]); ring.push([latlngs[0].lng, latlngs[0].lat]);
    return turf.polygon([ring]);
  }
  function centerLine(center, bearing, km) {
    const a = turf.destination(center, km, bearing + 180, { units: "kilometers" });
    const b = turf.destination(center, km, bearing, { units: "kilometers" });
    return turf.lineString([a.geometry.coordinates, b.geometry.coordinates]);
  }
  function clip(line, poly) {
    const split = turf.lineSplit(line, turf.polygonToLine(poly));
    return split.features.filter((s) => turf.booleanPointInPolygon(turf.along(s, turf.length(s, { units: "kilometers" }) / 2, { units: "kilometers" }), poly));
  }

  function generate(isAuto) {
    const tgt = current();
    const run = (ctx) => {
      const polyLayer = ctx.mode === "plan" ? ctx.plan.aoiLayer : aoi;
      const group = ctx.mode === "plan" ? ctx.plan.group : draft;
      if (!polyLayer) { setStatus("Draw AOI first"); return 0; }
      group.eachLayer((l) => { if (l !== polyLayer) group.removeLayer(l); });
      const h = heading(); const sM = spacing(); const ext = Math.max(0, Number(el.extend?.value || 0));
      const prefix = (el.prefix?.value || "L").trim() || "L"; const start = Math.max(1, Number(el.start?.value || 1));
      const poly = polyFromAOI(polyLayer); const bb = turf.bbox(poly);
      const diag = turf.distance(turf.point([bb[0], bb[1]]), turf.point([bb[2], bb[3]]), { units: "kilometers" });
      const base = centerLine(turf.center(poly), h, diag * 1.2 + ext / 1000); const step = sM / 1000; const n = Math.ceil((diag * 0.8) / step);
      const out = []; let idx = 0;
      for (let i = -n; i <= n; i += 1) {
        clip(turf.lineOffset(base, i * step, { units: "kilometers" }), poly).forEach((seg) => {
          idx += 1; seg.properties = { name: `${prefix}${String(start + idx - 1).padStart(3, "0")}`, heading: h, spacing_m: sM, extend_m: ext };
          out.push(seg); L.geoJSON(seg, { style: { color: "#60a5fa", weight: 2 } }).addTo(group);
        });
      }
      if (ctx.mode === "plan") {
        ctx.plan.features = out.slice();
        ctx.plan.params = { ...ctx.plan.params, heading: h, spacing: sM, extend: ext, prefix, start };
        buildPlanLabels(ctx.plan);
        syncGlobalPlan(ctx.plan);
      }
      else { lines = out.slice(); }
      return out.length;
    };
    if (tgt.mode === "multi") { tgt.plans.forEach((p) => run({ mode: "plan", plan: p })); renderPlans(); setStatus(`Updated ${tgt.plans.length} plans`); return; }
    const n = run(tgt);
    if (tgt.mode === "plan") renderPlans();
    setCount(n);
    if (!isAuto && n > 0) setStage("drafted");
    setStatus(n ? (isAuto ? "Lines updated" : "Lines generated") : "No lines (check AOI/spacing)");
  }

  function scheduleAuto() { if (!aoi && !activeId) return; if (autoTimer) clearTimeout(autoTimer); autoTimer = setTimeout(() => generate(true), 120); }

  async function runDepthAware() {
    if (!aoi) { setStatus("Depth Aware: draw AOI first"); return; }
    const c = aoi.getBounds().getCenter(); const src = (el.source?.value || "etopo").toLowerCase();
    const angle = Number(el.angle?.value || 140); const ov = Number(el.overlap?.value || 20);
    const depth = await identifyDepth(c.lat, c.lng, src);
    if (!Number.isFinite(depth)) { if (el.dap) el.dap.textContent = "Lookup failed"; return; }
    const sw = 2 * Math.abs(depth) * Math.tan((Math.max(0.0001, Math.min(179.9999, angle)) * Math.PI / 180) / 2);
    const spacingM = Math.max(1, Math.round(sw * (1 - Math.max(0, Math.min(0.99, ov / 100)))));
    setSpacing(spacingM); draftDA = { depthAware: true, depthSource: src, openingSwathAngle_deg: angle, overlap_pct: ov, sampledDepth_m: depth, computedSpacing_m: spacingM };
    if (el.dap) el.dap.textContent = `${src.toUpperCase()} depth ${depth.toFixed(1)} m -> spacing ${spacingM} m`;
    generate(false);
  }

  async function identifyDepth(lat, lng, src) {
    if (src === "gebco") {
      const base = "https://tiles.arcgis.com/tiles/C8EMgrsFcRFL6LrL/arcgis/rest/services/GEBCO_elevation_wgs84/ImageServer/identify";
      const url = `${base}?${new URLSearchParams({ f: "json", geometry: JSON.stringify({ x: lng, y: lat, spatialReference: { wkid: 4326 } }), geometryType: "esriGeometryPoint", sr: "4326", returnGeometry: "false" })}`;
      const r = await fetch(url); if (!r.ok) return null; const j = await r.json(); const v = j?.value ?? j?.properties?.value ?? j?.pixelValue; return Number.isFinite(Number(v)) ? Number(v) : null;
    }
    const base = "https://gis.ngdc.noaa.gov/arcgis/rest/services/DEM_mosaics/DEM_global_mosaic/ImageServer";
    const g = encodeURIComponent(JSON.stringify({ x: lng, y: lat, spatialReference: { wkid: 4326 } }));
    const r = await fetch(`${base}/identify?f=json&geometry=${g}&geometryType=esriGeometryPoint&sr=4326&returnGeometry=false`);
    if (!r.ok) return null; const j = await r.json(); const v = j?.value ?? j?.properties?.value ?? j?.pixelValue; return Number.isFinite(Number(v)) ? Number(v) : null;
  }

  function addPlan(name, aoiLatLngs, features, params) {
    const id = `lp_${Date.now().toString(36)}_${seq++}`; const group = L.featureGroup().addTo(plansRoot).addTo(map);
    const aoiLayer = L.polygon(aoiLatLngs, { color: "#a855f7", weight: 2, opacity: 0.9, fillOpacity: 0.08 }).addTo(group);
    const color = params?.color || "#60a5fa"; features.forEach((f) => L.geoJSON(f, { style: { color, weight: 2 } }).addTo(group));
    const plan = {
      id,
      name,
      group,
      aoiLayer,
      aoiGeoJSON: aoiLayer.toGeoJSON(),
      features: features.slice(),
      params: { ...params, ...(draftDA || {}) },
      color,
      opacity: Number.isFinite(Number(params?.opacity)) ? Number(params.opacity) : 1,
      _labelsEnabled: false,
      _labelMarkers: [],
    };
    applyPlanOpacity(plan, plan.opacity);
    plans.unshift(plan);
    registerGlobalPlan(plan);
    syncGlobalPlan(plan);
    draftDA = null;
    return plan;
  }

  function saveDraft() {
    if (!aoi) { setStatus("Draw AOI first"); return; }
    if (!lines.length) { setStatus("Generate lines first"); return; }
    const p = addPlan(`Plan ${plans.length + 1}`, (aoi.getLatLngs()[0] || aoi.getLatLngs()).slice(), lines.slice(), {
      heading: heading(), spacing: spacing(), extend: Math.max(0, Number(el.extend?.value || 0)), prefix: (el.prefix?.value || "L").trim() || "L", start: Math.max(1, Number(el.start?.value || 1)),
    });
    clearDraft();
    activeId = p.id;
    selected.clear();
    selected.add(p.id);
    renderPlans();
    setStatus("Saved");
    setStage("mode");
  }
  function exportSelection() {
    return selected.size ? plans.filter((p) => selected.has(p.id)) : plans.filter((p) => map.hasLayer(p.group));
  }
  function projectExportCRS(format) {
    const fmt = String(format || "geojson").toLowerCase();
    if (fmt === "gpx") return "EPSG:4326";
    const profileCrs = app.services.projections?.getProjectProfile?.()?.horizontalCRS;
    return profileCrs || "EPSG:4326";
  }
  function buildExportFeatureCollection(chosen) {
    const features = [];
    chosen.forEach((p) => {
      (p.features || []).forEach((f, i) => features.push({
        ...JSON.parse(JSON.stringify(f)),
        properties: { ...(f.properties || {}), plan_name: p.name, plan_id: p.id, plan_index: i + 1, feature_type: "line" },
      }));
      if (p.aoiGeoJSON) {
        features.push({
          ...JSON.parse(JSON.stringify(p.aoiGeoJSON)),
          properties: { ...(p.aoiGeoJSON.properties || {}), plan_name: p.name, plan_id: p.id, feature_type: "AOI" },
        });
      }
    });
    return { type: "FeatureCollection", features };
  }
  function prepareExportFC(chosen, format) {
    const raw = buildExportFeatureCollection(chosen);
    const targetCRS = projectExportCRS(format);
    try {
      const prepared = app.services.io?.prepareExportFeatureCollection?.(raw, {
        sourceCRS: "EPSG:4326",
        targetCRS,
        includeLegacyCrs: format === "geojson",
      });
      return {
        featureCollection: prepared?.featureCollection || raw,
        targetCRS: prepared?.targetCRS || targetCRS,
      };
    } catch (err) {
      console.error("[LinePlanning] projection export transform failed", err);
      app.ui.toast("Export transform failed, using WGS84 coordinates");
      return { featureCollection: raw, targetCRS: "EPSG:4326" };
    }
  }
  function flattenLineCoords(geometry) {
    if (!geometry || typeof geometry !== "object") return [];
    if (geometry.type === "LineString" && Array.isArray(geometry.coordinates)) return [geometry.coordinates];
    if (geometry.type === "MultiLineString" && Array.isArray(geometry.coordinates)) return geometry.coordinates.filter((c) => Array.isArray(c));
    return [];
  }
  function exportGeoJSON(featureCollection, targetCRS) {
    const out = {
      ...featureCollection,
      metadata: {
        ...(featureCollection.metadata || {}),
        export_crs: targetCRS,
        exported_at: new Date().toISOString(),
      },
    };
    return {
      filename: `hydrotools_lineplans_${Date.now()}.geojson`,
      mime: "application/geo+json",
      body: JSON.stringify(out, null, 2),
    };
  }
  function exportGPX(featureCollection) {
    const escXml = (s) => String(s).replace(/[<>&'"]/g, (ch) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", '"': "&quot;" }[ch]));
    const routes = [];
    (featureCollection?.features || [])
      .filter((f) => (f?.properties?.feature_type || "").toLowerCase() === "line")
      .forEach((f, i) => {
        flattenLineCoords(f?.geometry).forEach((coords, segIdx) => {
          const routeName = escXml(`${f?.properties?.plan_name || "Plan"} ${f?.properties?.name || `L${i + 1}`}${segIdx > 0 ? `-${segIdx + 1}` : ""}`);
          const pts = coords
            .filter((xy) => Array.isArray(xy) && Number.isFinite(Number(xy[0])) && Number.isFinite(Number(xy[1])))
            .map((xy) => `    <rtept lat="${Number(xy[1]).toFixed(8)}" lon="${Number(xy[0]).toFixed(8)}"></rtept>`)
            .join("\n");
          if (!pts) return;
          routes.push(`  <rte>\n    <name>${routeName}</name>\n${pts}\n  </rte>`);
        });
      });
    return {
      filename: `hydrotools_lineplans_${Date.now()}.gpx`,
      mime: "application/gpx+xml",
      body: `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" creator="HydroTools" xmlns="http://www.topografix.com/GPX/1/1">\n  <metadata>\n    <name>HydroTools Line Plans</name>\n    <time>${new Date().toISOString()}</time>\n  </metadata>\n${routes.join("\n")}\n</gpx>\n`,
    };
  }
  function exportDXF(featureCollection) {
    const out = [
      "0", "SECTION",
      "2", "HEADER",
      "0", "ENDSEC",
      "0", "SECTION",
      "2", "ENTITIES",
    ];
    (featureCollection?.features || [])
      .filter((f) => (f?.properties?.feature_type || "").toLowerCase() === "line")
      .forEach((f) => {
        flattenLineCoords(f?.geometry).forEach((coords) => {
          const pts = coords.filter((xy) => Array.isArray(xy) && Number.isFinite(Number(xy[0])) && Number.isFinite(Number(xy[1])));
          if (pts.length < 2) return;
          out.push("0", "POLYLINE", "8", "LINEPLANS", "66", "1", "70", "0");
          pts.forEach((xy) => {
            out.push("0", "VERTEX", "8", "LINEPLANS", "10", `${Number(xy[0]).toFixed(8)}`, "20", `${Number(xy[1]).toFixed(8)}`, "30", "0.0");
          });
          out.push("0", "SEQEND");
        });
      });
    out.push("0", "ENDSEC", "0", "EOF");
    return {
      filename: `hydrotools_lineplans_${Date.now()}.dxf`,
      mime: "application/dxf",
      body: `${out.join("\n")}\n`,
    };
  }
  function exportLNW(featureCollection) {
    const out = [];
    const lines = [];
    (featureCollection?.features || [])
      .filter((f) => (f?.properties?.feature_type || "").toLowerCase() === "line")
      .forEach((f, i) => {
        flattenLineCoords(f?.geometry).forEach((coords, segIdx) => {
          const pts = coords.filter((xy) => Array.isArray(xy) && Number.isFinite(Number(xy[0])) && Number.isFinite(Number(xy[1])));
          if (pts.length < 2) return;
          lines.push({
            name: `${f?.properties?.plan_name || "Plan"}_${f?.properties?.name || `L${i + 1}`}${segIdx > 0 ? `_${segIdx + 1}` : ""}`,
            pts,
          });
        });
      });
    out.push(`LNS ${lines.length}`);
    lines.forEach((ln) => {
      out.push(`LIN ${ln.pts.length}`);
      ln.pts.forEach((xy) => out.push(`PTS ${Number(xy[0]).toFixed(8)} ${Number(xy[1]).toFixed(8)}`));
      out.push(`LNN ${ln.name}`);
      out.push("EOL");
    });
    return {
      filename: `hydrotools_lineplans_${Date.now()}.lnw`,
      mime: "text/plain",
      body: `${out.join("\n")}\n`,
    };
  }
  function exportSelected() {
    const chosen = exportSelection();
    if (!chosen.length) return;
    const fmt = (el.exportFmt?.value || "geojson").toLowerCase();
    const prepared = prepareExportFC(chosen, fmt);
    let payload = exportGeoJSON(prepared.featureCollection, prepared.targetCRS);
    if (fmt === "gpx") payload = exportGPX(prepared.featureCollection);
    if (fmt === "dxf") payload = exportDXF(prepared.featureCollection);
    if (fmt === "lnw") payload = exportLNW(prepared.featureCollection);
    download(payload.filename, new Blob([payload.body], { type: payload.mime }));
    setStatus(`Exported ${chosen.length} plan${chosen.length === 1 ? "" : "s"} as ${fmt.toUpperCase()} (${prepared.targetCRS})`);
  }
  function updateExportSelectedEnabled() {
    if (!el.exportSel) return;
    const chosen = selected.size ? plans.filter((p) => selected.has(p.id)) : plans.filter((p) => map.hasLayer(p.group));
    el.exportSel.disabled = chosen.length === 0;
  }
  function download(filename, blob) { const a = document.createElement("a"); const u = URL.createObjectURL(blob); a.href = u; a.download = filename; document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(u); a.remove(); }, 250); }

  function renderPlans() {
    if (!el.planList) return;
    if (!plans.length) {
      el.planList.innerHTML = '<div class="muted" style="font-size:11px;">No saved plans yet. Draw an AOI and click <b>Generate lines</b>.</div>';
      if (el.exportSel) el.exportSel.disabled = true;
      return;
    }

    const speed = Math.max(0, Number(el.speed?.value || 0) || 0);
    const planLnm = (p) => {
      try {
        const km = (p.features || []).reduce((sum, f) => sum + turf.length(f, { units: "kilometers" }), 0);
        return km / 1.852;
      } catch {
        return NaN;
      }
    };
    const planRunH = (lnm) => (speed > 0 ? lnm / speed : NaN);
    const planSqNm = (p) => {
      try {
        const m2 = turf.area(p.aoiGeoJSON);
        return m2 / (1852 * 1852);
      } catch {
        return NaN;
      }
    };
    const planTurnH = (p) => {
      if (speed <= 0) return NaN;
      const nLines = p.features?.length || 0;
      const turns = Math.max(0, nLines - 1);
      const spacingM = Math.max(0, Number(p.params?.spacing || 0));
      const turnNm = (Math.PI * (spacingM / 2)) / 1852;
      return (turnNm * turns) / speed;
    };
    const fmtNm = (nm) => (!Number.isFinite(nm) ? "--" : nm < 10 ? nm.toFixed(2) : nm < 100 ? nm.toFixed(1) : Math.round(nm).toString());
    const fmtDur = (hours) => {
      if (!Number.isFinite(hours) || hours < 0) return "--";
      const totalMin = Math.round(hours * 60);
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      return h <= 0 ? `${m}m` : `${h}h ${String(m).padStart(2, "0")}m`;
    };

    const totals = plans.reduce((acc, p) => {
      const lnm = planLnm(p);
      const sqnm = planSqNm(p);
      const runH = planRunH(lnm);
      const turnH = planTurnH(p);
      acc.lines += p.features?.length || 0;
      if (Number.isFinite(lnm)) acc.lnm += lnm;
      if (Number.isFinite(sqnm)) acc.sqnm += sqnm;
      if (Number.isFinite(runH)) acc.runH += runH;
      if (Number.isFinite(turnH)) acc.turnH += turnH;
      return acc;
    }, { lines: 0, lnm: 0, sqnm: 0, runH: 0, turnH: 0 });

    const header = `
      <div class="lp-plan-header lp-plan-grid">
        <div></div>
        <div>Name</div>
        <div style="text-align:right">#</div>
        <div style="text-align:right">LNM</div>
        <div style="text-align:right">SqNM</div>
        <div style="text-align:right">Run</div>
        <div style="text-align:right">Turn</div>
        <div>Clr</div>
        <div>Lbl</div>
        <div>↓</div>
        <div>x</div>
      </div>
    `;

    const rows = plans.map((p) => {
      const lnm = planLnm(p);
      const sqnm = planSqNm(p);
      const runH = planRunH(lnm);
      const turnH = planTurnH(p);
      const checked = map.hasLayer(p.group) ? "checked" : "";
      const style = selected.has(p.id)
        ? 'style="outline:1px solid rgba(96,165,250,.65);background:rgba(2,6,23,.18);border-radius:8px;padding:4px 6px;"'
        : 'style="border-radius:8px;padding:4px 6px;"';
      return `
        <div class="lp-plan-row" data-id="${p.id}" ${style}>
          <div class="lp-plan-grid">
            <input class="lp-plan-vis" type="checkbox" data-vis="${p.id}" ${checked}>
            ${editingPlanId === p.id
              ? `<input class="lp-plan-name-edit" data-rename-input="${p.id}" value="${esc(p.name)}" />`
              : `<button class="lp-plan-name" data-sel="${p.id}">${p.name}</button>`}
            <span class="muted lp-plan-meta">${p.features?.length || 0}</span>
            <span class="muted lp-plan-meta">${fmtNm(lnm)}</span>
            <span class="muted lp-plan-meta">${fmtNm(sqnm)}</span>
            <span class="muted lp-plan-meta">${fmtDur(runH)}</span>
            <span class="muted lp-plan-meta">${fmtDur(turnH)}</span>
            <input class="lp-plan-color" type="color" data-color="${p.id}" value="${p.color || "#60a5fa"}">
            <input type="checkbox" data-lbl="${p.id}" ${p._labelsEnabled ? "checked" : ""}>
            <button class="iconbtn" data-exp="${p.id}">↓</button>
            <button class="iconbtn danger" data-del="${p.id}">x</button>
          </div>
        </div>
      `;
    }).join("");

    const footer = `
      <div class="lp-plan-sum lp-plan-grid">
        <div></div>
        <div class="lp-sum-label">Total</div>
        <div style="text-align:right">${totals.lines}</div>
        <div style="text-align:right">${fmtNm(totals.lnm)}</div>
        <div style="text-align:right">${fmtNm(totals.sqnm)}</div>
        <div style="text-align:right">${fmtDur(totals.runH)}</div>
        <div style="text-align:right">${fmtDur(totals.turnH)}</div>
        <div></div><div></div><div></div><div></div>
      </div>
    `;

    el.planList.innerHTML = `<div class="lp-plan-table">${header}${rows}${footer}</div>`;
    updateExportSelectedEnabled();
    el.planList.querySelectorAll("[data-vis]").forEach((n) => (n.onchange = (e) => {
      const p = plans.find((x) => x.id === e.target.dataset.vis);
      if (!p) return;
      if (p.globalLayerId) {
        inGlobalSync = true;
        try { app.services.layers.setVisible(p.globalLayerId, !!e.target.checked); } finally { inGlobalSync = false; }
      } else if (e.target.checked) {
        p.group.addTo(map);
        if (p._labelsEnabled) buildPlanLabels(p);
      } else {
        map.removeLayer(p.group);
      }
      syncGlobalPlan(p);
      updateExportSelectedEnabled();
    }));
    el.planList.querySelectorAll("[data-lbl]").forEach((n) => (n.onchange = (e) => { const p = plans.find((x) => x.id === e.target.dataset.lbl); if (!p) return; p._labelsEnabled = !!e.target.checked; if (p._labelsEnabled) buildPlanLabels(p); else clearPlanLabels(p); }));
    el.planList.querySelectorAll("[data-sel]").forEach((n) => (n.onclick = (e) => { e.preventDefault(); const id = e.target.dataset.sel; if (e.ctrlKey || e.metaKey) { if (selected.has(id)) selected.delete(id); else selected.add(id); activeId = id; } else { selected.clear(); selected.add(id); activeId = id; } renderPlans(); updateExportSelectedEnabled(); }));
    el.planList.querySelectorAll("[data-sel]").forEach((n) => (n.ondblclick = (e) => {
      e.preventDefault();
      const id = e.target.dataset.sel;
      editingPlanId = id;
      renderPlans();
      setTimeout(() => {
        const input = el.planList?.querySelector?.(`[data-rename-input="${id}"]`);
        input?.focus?.();
        input?.select?.();
      }, 0);
    }));
    el.planList.querySelectorAll("[data-rename-input]").forEach((n) => {
      n.onkeydown = (e) => {
        const id = e.target.dataset.renameInput;
        if (!id) return;
        if (e.key === "Enter") {
          e.preventDefault();
          const p = plans.find((x) => x.id === id);
          if (p) {
            const next = String(e.target.value || "").trim();
            if (next) p.name = next;
            syncGlobalPlan(p);
          }
          editingPlanId = null;
          renderPlans();
        } else if (e.key === "Escape") {
          e.preventDefault();
          editingPlanId = null;
          renderPlans();
        }
      };
      n.onblur = (e) => {
        const id = e.target.dataset.renameInput;
        if (!id) return;
        const p = plans.find((x) => x.id === id);
        if (p) {
          const next = String(e.target.value || "").trim();
          if (next) p.name = next;
          syncGlobalPlan(p);
        }
        editingPlanId = null;
        renderPlans();
      };
    });
    el.planList.querySelectorAll("[data-color]").forEach((n) => (n.onchange = (e) => {
      const p = plans.find((x) => x.id === e.target.dataset.color);
      if (!p) return;
      p.color = (e.target.value || "#60a5fa").toLowerCase();
      p.params = p.params || {};
      p.params.color = p.color;
      p.group.eachLayer((l) => { if (l !== p.aoiLayer && l.setStyle) l.setStyle({ color: p.color }); });
      syncGlobalPlan(p);
    }));
    el.planList.querySelectorAll("[data-exp]").forEach((n) => (n.onclick = (e) => { const p = plans.find((x) => x.id === e.target.dataset.exp); if (!p) return; selected.clear(); selected.add(p.id); exportSelected(); }));
    el.planList.querySelectorAll("[data-del]").forEach((n) => (n.onclick = (e) => { removePlan(e.target.dataset.del, { fromGlobal: false }); }));
  }

  function snapshot() {
    return {
      plans: plans.map((p) => ({
        id: p.id,
        name: p.name,
        aoiGeoJSON: p.aoiGeoJSON,
        features: p.features,
        params: p.params,
        color: p.color,
        opacity: p.opacity ?? 1,
        visible: map.hasLayer(p.group),
        labelsEnabled: !!p._labelsEnabled,
      })),
      activeId,
      selected: [...selected],
    };
  }
  function restore(data) {
    if (!data || typeof data !== "object") return;
    if (plans.length) {
      inGlobalSync = true;
      try {
        plans.forEach((p) => {
          clearPlanLabels(p);
          try { map.removeLayer(p.group); } catch {}
          try { plansRoot.removeLayer(p.group); } catch {}
          if (!p?.globalLayerId) return;
          try { app.services.layers.removeLayer(p.globalLayerId); } catch {}
        });
      } finally {
        inGlobalSync = false;
      }
      plans.splice(0, plans.length);
    }
    clearAll();
    (Array.isArray(data.plans) ? data.plans : []).forEach((p) => {
      const ring = p?.aoiGeoJSON?.geometry?.coordinates?.[0];
      if (!ring || !Array.isArray(p.features)) return;
      const latlngs = ring.map(([lng, lat]) => L.latLng(lat, lng));
      const plan = addPlan(p.name || `Plan ${plans.length + 1}`, latlngs, p.features, { ...(p.params || {}), opacity: Number.isFinite(Number(p.opacity)) ? Number(p.opacity) : 1 });
      plan.color = p.color || plan.color;
      plan._labelsEnabled = !!p.labelsEnabled;
      if (plan._labelsEnabled) buildPlanLabels(plan);
      if (!p.visible) map.removeLayer(plan.group);
      syncGlobalPlan(plan);
    });
    activeId = data.activeId || null;
    selected.clear(); (data.selected || []).forEach((id) => selected.add(id));
    renderPlans();
  }

  // ── Statistical Estimator ────────────────────────────────────────────────

  function statCoverage() { return Math.max(1, Number(statEl.coverage?.value || 4) || 4); }
  function statCrossline() { return Math.max(0, Number(statEl.crossline?.value || 4) || 4); }
  function statShoal() { return Math.max(0, Number(statEl.shoal?.value || 0) || 0); }
  function statRunIn() { return Math.max(0, Number(statEl.runin?.value || 200) || 200); }
  function statSource() { return String(statEl.source?.value || "etopo").toLowerCase(); }
  function setStatStatus(s) { if (statEl.status) statEl.status.textContent = s; }

  function computeStatRow(areaAcres, avgDepthFt, coverageXwd, crosslinePct, shoalPct, runInM) {
    const avgDepthM = avgDepthFt * 0.3048;
    const areaM2 = areaAcres * 4046.85642;
    const swathM = coverageXwd * avgDepthM;
    const sqrtArea = Math.sqrt(areaM2);
    const numLines = Math.ceil(sqrtArea / swathM) + 2;
    const lineLengthM = sqrtArea + runInM;
    const mainschemeM = numLines * lineLengthM;
    const crosslineM = mainschemeM * (crosslinePct / 100);
    const shoalM = (mainschemeM + crosslineM) * (shoalPct / 100);
    const totalM = mainschemeM + crosslineM + shoalM;
    return {
      avgDepthM,
      areaM2,
      swathM,
      numLines,
      lineLengthM,
      mainschemeNm: mainschemeM * 0.000539957,
      crosslineNm: crosslineM * 0.000539957,
      shoalNm: shoalM * 0.000539957,
      totalNm: totalM * 0.000539957,
    };
  }

  async function samplePolygonAvgDepth(turfPoly, src) {
    const coords = turfPoly.geometry.coordinates[0];
    const geom = {
      rings: [coords.map(([x, y]) => [x, y])],
      spatialReference: { wkid: 4326 },
    };
    const url = src === "gebco" ? GEBCO_SAMPLES_URL : ETOPO_SAMPLES_URL;
    const params = new URLSearchParams({
      geometry: JSON.stringify(geom),
      geometryType: "esriGeometryPolygon",
      sampleCount: "25",
      f: "json",
    });
    const res = await fetch(`${url}?${params}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json.error) throw new Error(json.error.message || "Service error");
    const depths = (json.samples || [])
      .map((s) => Number(Array.isArray(s.value) ? s.value[0] : s.value))
      .filter((v) => Number.isFinite(v) && v <= 0);  // bathymetric values are ≤ 0
    if (!depths.length) throw new Error("No valid depth samples returned");
    const avgM = Math.abs(depths.reduce((a, b) => a + b, 0) / depths.length);
    return avgM / 0.3048;  // return in feet to match spreadsheet convention
  }

  async function estimateStatAOI(entry) {
    entry.loading = true;
    entry.error = null;
    entry.result = null;
    renderStatTable();
    try {
      const src = statSource();
      const depthFt = entry.depthFtOverride != null
        ? entry.depthFtOverride
        : await samplePolygonAvgDepth(entry.turfPoly, src);
      entry.sampledDepthFt = depthFt;
      const cov = statCoverage();
      const xl = statCrossline();
      const sh = statShoal();
      const ri = statRunIn();
      entry.result = computeStatRow(entry.areaAcres, depthFt, cov, xl, sh, ri);
    } catch (err) {
      entry.error = String(err?.message || err);
    }
    entry.loading = false;
    renderStatTable();
  }

  function drawStatAOI() {
    setStatStatus("Click to add vertices, double-click to finish");
    const was = map.doubleClickZoom?.enabled?.() ?? false;
    map.doubleClickZoom?.disable?.();
    syncCursor(true);
    const pts = [];
    const temp = L.polygon([], { color: "#f59e0b", weight: 2, opacity: 0.8, fillOpacity: 0.1, dashArray: "6 4" }).addTo(sketch);
    const onClick = (e) => { pts.push(e.latlng); temp.setLatLngs(pts); };
    const onDbl = (e) => {
      L.DomEvent.stop(e);
      map.off("click", onClick);
      map.off("dblclick", onDbl);
      sketch.removeLayer(temp);
      if (was) setTimeout(() => map.doubleClickZoom?.enable?.(), 0);
      syncCursor(false);
      if (pts.length < 3) { setStatStatus("Need ≥ 3 vertices"); return; }
      const layer = L.polygon(pts, { color: "#f59e0b", weight: 2, opacity: 0.9, fillOpacity: 0.08 }).addTo(draft);
      const ring = pts.map((p) => [p.lng, p.lat]);
      ring.push([pts[0].lng, pts[0].lat]);
      const turfPoly = turf.polygon([ring]);
      const areaM2 = turf.area(turfPoly);
      const areaAcres = areaM2 / 4046.85642;
      const entry = {
        id: `stat_${Date.now().toString(36)}_${statSeq++}`,
        name: `Area ${statAOIs.length + 1}`,
        layer,
        turfPoly,
        areaAcres,
        depthFtOverride: null,
        sampledDepthFt: null,
        loading: false,
        error: null,
        result: null,
      };
      statAOIs.push(entry);
      setStatStatus("Estimating depth…");
      estimateStatAOI(entry);
    };
    map.on("click", onClick);
    map.on("dblclick", onDbl);
  }

  function clearStatAOIs() {
    statAOIs.forEach((e) => { try { draft.removeLayer(e.layer); } catch {} });
    statAOIs.splice(0, statAOIs.length);
    renderStatTable();
    setStatStatus("Cleared");
  }

  function recomputeAllStat() {
    statAOIs.forEach((e) => {
      if (!e.loading) {
        const depthFt = e.depthFtOverride ?? e.sampledDepthFt;
        if (depthFt != null) {
          e.result = computeStatRow(e.areaAcres, depthFt, statCoverage(), statCrossline(), statShoal(), statRunIn());
        }
      }
    });
    renderStatTable();
  }

  function renderStatTable() {
    const el2 = statEl.table;
    if (!el2) return;
    if (!statAOIs.length) {
      el2.innerHTML = '<div class="muted" style="font-size:11px;">Draw AOI polygons to estimate LNM.</div>';
      return;
    }
    const fmt1 = (v) => Number.isFinite(v) ? v.toFixed(1) : "—";
    const fmt0 = (v) => Number.isFinite(v) ? Math.round(v).toString() : "—";
    const fmt2 = (v) => Number.isFinite(v) ? v.toFixed(2) : "—";
    const thStyle = "padding:3px 5px;font-size:10px;font-weight:600;white-space:nowrap;color:var(--muted);border-bottom:1px solid var(--border);text-align:right;";
    const thL = `${thStyle}text-align:left;`;
    const tdStyle = "padding:2px 5px;white-space:nowrap;text-align:right;";
    const tdL = `${tdStyle}text-align:left;`;

    let mainTot = 0, xlTot = 0, shoalTot = 0, totalTot = 0;
    const rows = statAOIs.map((e) => {
      const r = e.result;
      if (r) { mainTot += r.mainschemeNm; xlTot += r.crosslineNm; shoalTot += r.shoalNm; totalTot += r.totalNm; }
      const depthFt = e.depthFtOverride ?? e.sampledDepthFt;
      const depthCell = e.loading
        ? `<td style="${tdStyle}"><span style="color:var(--muted)">…</span></td>`
        : `<td style="${tdStyle}"><input type="number" data-stat-depth="${e.id}" value="${depthFt != null ? depthFt.toFixed(1) : ""}" placeholder="—" style="width:52px;font-size:10px;text-align:right;" step="0.5" /></td>`;
      return `<tr style="border-bottom:1px solid rgba(148,163,184,0.08);">
        <td style="${tdL}">
          <span style="font-size:10px;">${esc(e.name)}</span>
          <button data-stat-del="${e.id}" style="margin-left:4px;font-size:9px;background:none;border:none;color:#f87171;cursor:pointer;" title="Remove">✕</button>
          <button data-stat-zoom="${e.id}" style="margin-left:2px;font-size:9px;background:none;border:none;color:var(--muted);cursor:pointer;" title="Zoom to">⌖</button>
        </td>
        <td style="${tdStyle}">${fmt1(e.areaAcres)}</td>
        ${depthCell}
        <td style="${tdStyle}">${e.error ? `<span style="color:#f87171" title="${esc(e.error)}">!</span>` : fmt1(r?.avgDepthM)}</td>
        <td style="${tdStyle}">${fmt0(r?.swathM)}</td>
        <td style="${tdStyle}">${fmt0(r?.numLines)}</td>
        <td style="${tdStyle}">${fmt2(r?.mainschemeNm)}</td>
        <td style="${tdStyle}">${fmt2(r?.crosslineNm)}</td>
        <td style="${tdStyle}" style="font-weight:600;">${fmt2(r?.totalNm)}</td>
      </tr>`;
    }).join("");

    const totRow = `<tr style="border-top:2px solid var(--border);font-weight:700;">
      <td style="${tdL}">Total</td>
      <td style="${tdStyle}"></td><td style="${tdStyle}"></td><td style="${tdStyle}"></td>
      <td style="${tdStyle}"></td><td style="${tdStyle}"></td>
      <td style="${tdStyle}">${fmt2(mainTot)}</td>
      <td style="${tdStyle}">${fmt2(xlTot)}</td>
      <td style="${tdStyle}">${fmt2(totalTot)}</td>
    </tr>`;

    el2.innerHTML = `
      <table style="width:100%;border-collapse:collapse;font-size:11px;">
        <thead><tr>
          <th style="${thL}">Name</th>
          <th style="${thStyle}">Acres</th>
          <th style="${thStyle}">Avg (ft)</th>
          <th style="${thStyle}">Avg (m)</th>
          <th style="${thStyle}">Swath (m)</th>
          <th style="${thStyle}"># Lines</th>
          <th style="${thStyle}">Main (nm)</th>
          <th style="${thStyle}">XL (nm)</th>
          <th style="${thStyle}">Total (nm)</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot>${totRow}</tfoot>
      </table>`;

    el2.querySelectorAll("[data-stat-del]").forEach((btn) => {
      btn.onclick = () => {
        const i = statAOIs.findIndex((e) => e.id === btn.dataset.statDel);
        if (i < 0) return;
        try { draft.removeLayer(statAOIs[i].layer); } catch {}
        statAOIs.splice(i, 1);
        renderStatTable();
      };
    });
    el2.querySelectorAll("[data-stat-zoom]").forEach((btn) => {
      btn.onclick = () => {
        const e = statAOIs.find((x) => x.id === btn.dataset.statZoom);
        if (!e) return;
        try { map.fitBounds(e.layer.getBounds(), { padding: [30, 30] }); } catch {}
      };
    });
    el2.querySelectorAll("[data-stat-depth]").forEach((inp) => {
      inp.onchange = () => {
        const e = statAOIs.find((x) => x.id === inp.dataset.statDepth);
        if (!e) return;
        const v = Number(inp.value);
        e.depthFtOverride = Number.isFinite(v) && v > 0 ? v : null;
        const depthFt = e.depthFtOverride ?? e.sampledDepthFt;
        if (depthFt != null) {
          e.result = computeStatRow(e.areaAcres, depthFt, statCoverage(), statCrossline(), statShoal(), statRunIn());
        }
        renderStatTable();
      };
    });
  }

  function bindStatSection() {
    statEl.draw = container.querySelector("#lp-stat-draw");
    statEl.clearAll = container.querySelector("#lp-stat-clear-all");
    statEl.status = container.querySelector("#lp-stat-status");
    statEl.table = container.querySelector("#lp-stat-table");
    statEl.coverage = container.querySelector("#lp-stat-coverage");
    statEl.crossline = container.querySelector("#lp-stat-crossline");
    statEl.shoal = container.querySelector("#lp-stat-shoal");
    statEl.runin = container.querySelector("#lp-stat-runin");
    statEl.source = container.querySelector("#lp-stat-source");

    statEl.draw?.addEventListener("click", drawStatAOI);
    statEl.clearAll?.addEventListener("click", clearStatAOIs);
    [statEl.coverage, statEl.crossline, statEl.shoal, statEl.runin].forEach((inp) => {
      inp?.addEventListener("change", recomputeAllStat);
    });
    renderStatTable();
  }

  return {
    mount,
    destroy: () => {
      if (autoTimer) clearTimeout(autoTimer);
      if (plans.length) {
        inGlobalSync = true;
        try {
          plans.forEach((p) => {
            clearPlanLabels(p);
            if (!p?.globalLayerId) return;
            try { app.services.layers.removeLayer(p.globalLayerId); } catch {}
          });
        } finally {
          inGlobalSync = false;
        }
        plans.splice(0, plans.length);
      }
      sketch.clearLayers();
      draft.clearLayers();
      plansRoot.clearLayers();
      statAOIs.splice(0, statAOIs.length);
    },
    snapshot,
    restore,
  };
}
